import React, {JSX} from 'react';
import styled, {IStyledComponent} from 'styled-components/native';
import {useTheme} from 'styled-components';
import TextGeneric from '../../textGeneric';
import {Theme} from '../../../theme/lightTheme';
import MyPressable from '../../myPressable';
import Animated, {FadeIn} from 'react-native-reanimated';
import Icon from '../../icon';
import {MultiSelectItemProps} from '../../../interfaces/components/multiSelectPicker';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';

const Container = styled(MyPressable)`
  width: 100%;
  align-items: center;
  padding: 2.5% 2%;
  border-radius: 2px;
  flex: 1;
`;
const BorderContainer: IStyledComponent<'native', any> = styled.View`
  align-items: center;
  justify-content: center;
  border-width: 1px;
  width: ${({theme}) => theme.text.s55}px;
  aspect-ratio: 1;
  border-radius: 6px;
  border-color: ${({theme, selected}) =>
    selected ? theme.primary : theme.text.grey + '44'};
`;
const CheckIconContainer: IStyledComponent<'native', any> = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const Label = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.dark};
  flex: 1;
`;
const InfoIconContainer = styled.TouchableOpacity`
  margin-left: auto;
  padding-left: 5%;
`;

/**
 * MultiSelectItem is a component designed for rendering an item within a multi-select picker interface.
 * It allows users to select or deselect an item through a touchable interface, visually indicating the
 * current state (selected or not) with a check mark. The component also supports displaying additional
 * information about the item through a tooltip that can be toggled on or off.
 *
 * @component
 * @param {Object} props - Properties passed to the MultiSelectItem component.
 * @param {ViewStyle} props.containerStyle - Custom styles for the item's container.
 * @param {ReactNode | string} props.description - Optional description or additional information about the item, displayed in a tooltip.
 * @param {boolean} props.disabled - If true, the item is displayed as disabled and cannot be interacted with.
 * @param {string} props.id - A unique identifier for the item.
 * @param {ReactNode | string} props.label - The label displayed next to the selection control.
 * @param {TextStyle} props.labelStyle - Custom styles for the item's label.
 * @param {'row' | 'column'} props.layout - The layout orientation of the label relative to the selection control.
 * @param {Function} props.onPress - Callback function triggered when the item is selected or deselected.
 * @param {boolean} props.selected - Indicates if the item is currently selected.
 * @returns {JSX.Element} The rendered MultiSelectItem component.
 */
const MultiSelectItem: React.FC<MultiSelectItemProps> = ({
  containerStyle = {},
  description,
  disabled = false,
  id,
  label,
  labelStyle,
  layout,
  onPress,
  selected = false,
  isTipHidden,
  withBorder,
  isTouchableOpacity,
  checkStyle,
}: MultiSelectItemProps): JSX.Element => {
  const theme = useTheme() as Theme;
  let orientation: any = {flexDirection: 'row'};
  let margin: any = {marginLeft: 10};

  if (layout === 'column') {
    orientation = {alignItems: 'center'};
    margin = {marginTop: 10};
  }

  function handlePress() {
    if (onPress) {
      onPress(id);
    }
  }

  const labelComp = React.isValidElement(label) ? (
    label
  ) : label ? (
    <Label style={[margin, labelStyle]}>{label}</Label>
  ) : null;
  return (
    <Animated.View entering={FadeIn} style={[{width: '100%'}, containerStyle]}>
      <Container
        accessibilityHint={
          typeof description === 'string' ? description : undefined
        }
        iosOpacityEffect={isTouchableOpacity}
        accessibilityLabel={typeof label === 'string' ? label : undefined}
        accessibilityRole={'checkbox'}
        accessibilityState={{checked: selected, disabled}}
        disabled={disabled}
        onPress={handlePress}
        style={[orientation, {opacity: disabled ? 0.2 : 1}]}
        borderRadius={4}
        containerStyle={[
          {
            width: '100%',
            borderRadius: 4,
            flex: 1,
          },
          withBorder && {
            borderWidth: 1,
            borderColor: selected ? theme.primary : theme.text.grey + '44',
            backgroundColor: selected ? theme.primary + '11' : 'transparent',
          },
          containerStyle,
        ]}>
        <BorderContainer style={checkStyle} selected={selected}>
          {selected && (
            <CheckIconContainer>
              <Icon name={'checkSelect'} color={theme.primary} />
            </CheckIconContainer>
          )}
        </BorderContainer>
        {labelComp}
        {!!description && !isTipHidden && (
          <InfoIconContainer
            onPress={() =>
              openQuestionModal({
                title: 'Info',
                message: description || '',
                type: 'info',
              })
            }>
            <Icon name={'info'} color={theme.primary} size={theme.text.s5} />
          </InfoIconContainer>
        )}
      </Container>
    </Animated.View>
  );
};

export default MultiSelectItem;
